import { generalTooltipText } from '../helpers/tooltip';
import firstCharToUpper from '../helpers/stringFormat';
import { makePlural } from '../helpers/stringFormat';
import { getMultipleInitialValues, getSliderInitialValue, getSliderInitialValues } from './formFunctions';
import { formatString } from './valueFunctions';

const annualContractValue = {
    name: 'annual_contract_value',
    label: 'Annual Contract Value (ACV)',
    description: generalTooltipText.acv,
    unit: '€',
    adornment: 'start',
    width: 'half',
    guided: false,
    compare: false
};

const contributionMargin = {
    name: 'contribution_margin',
    label: 'Contribution margin',
    description: generalTooltipText.contributionMargin,
    unit: '%',
    adornment: 'end',
    width: 'half',
    guided: false,
    compare: false
};

const transactionValue = {
    name: 'transaction_value',
    label: 'Transaction value',
    description: generalTooltipText.transactionValue,
    unit: '€',
    adornment: 'start',
    width: 'half',
    guided: false,
    compare: false
};

const transactionFrequency = {
    name: 'transaction_frequency',
    label: 'Annual transaction frequency',
    description: generalTooltipText.transactionFrequency,
    unit: 'times/year',
    adornment: 'end',
    width: 'half',
    guided: false,
    compare: false
};

const riskReduction = {
    name: 'risk_reduction',
    label: 'Risk Reduction',
    description: 'Risk Reduction when using your product instead of the competitive alternative.',
    unit: '%',
    adornment: 'end',
    width: 'full',
    guided: false,
    compare: true
};

const metricsConfig = {
    "sales-driven_revenue": {
        purpose: "Quantify how your product increases sales-driven revenue vs. the competitive alternative by raising conversion rates across the funnel and accelerating deal closure.",
        introText: "The following driver was identified as potentially impacted by this outcome—click Add Driver to quantify conversion uplift and expected improvement vs. the competitive alternative.",
        driverPath: "sales_driven",
        driverType: "revenue",
        type: "sales",
        has_revenue_model: true,
        metrics: {
            subscription: [
                annualContractValue,
                contributionMargin,
                {
                    name: 'conversion.outbound',
                    label: 'Outbound to Meeting conversion',
                    description: 'Outbound to meeting conversion measures the percentage of outbound contacts that result in scheduled meetings, which is the effectiveness of your outreach efforts in engaging prospects.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'conversion_increase.outbound',
                    label: 'Outbound conversion Increase',
                    description: 'Increased outbound to meeting conversion rate when using your product campared to the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
                {
                    name: 'conversion.inbound',
                    label: 'Inbound to Meeting conversion',
                    description: 'Inbound to meeting conversion measures the percentage of inbound leads that progress to scheduled meetings, reflecting the effectiveness of your lead capture and nurturing process in engaging potential customers.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'conversion_increase.inbound',
                    label: 'Inbound conversion Increase',
                    description: 'Increased inbound to meeting conversion rate when using your product campared to the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
                {
                    name: 'conversion.demo',
                    label: 'Meeting to Demo conversion',
                    description: 'Meeting to Demo conversion measures the percentage of meetings that progress into product demonstrations, reflecting the effectiveness of turning initial engagements into opportunities to showcase your solution.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'conversion_increase.demo',
                    label: 'Demo conversion Increase',
                    description: 'Increased meeting to demo conversion rate when using your product campared to the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
                {
                    name: 'conversion.sales',
                    label: 'Demo to Sales conversion',
                    description: 'Demo to Sales conversion measures the percentage of product demonstrations that result in a closed sale, reflecting the effectiveness of your demos in converting prospects into customers.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'conversion_increase.sales',
                    label: 'Sales conversion Increase',
                    description: 'Increased demo to sales conversion rate when using your product campared to the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
            ],
            transactional: [
                transactionValue,
                transactionFrequency,
                contributionMargin,
                {
                    name: 'conversion.outbound',
                    label: 'Outbound to Meeting conversion',
                    description: 'Outbound to meeting conversion measures the percentage of outbound contacts that result in scheduled meetings, which is the effectiveness of your outreach efforts in engaging prospects.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'conversion_increase.outbound',
                    label: 'Increased outbound to meeting conversion rate when using your product campared to the competitive alternative.',
                    description: 'Revenue loss quantifies the percentage reduction in revenue, reflecting the financial impact of lost sales or declining performance.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
                {
                    name: 'conversion.inbound',
                    label: 'Inbound to Meeting conversion',
                    description: 'Inbound to meeting conversion measures the percentage of inbound leads that progress to scheduled meetings, reflecting the effectiveness of your lead capture and nurturing process in engaging potential customers.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'conversion_increase.inbound',
                    label: 'Inbound conversion Increase',
                    description: 'Increased inbound to meeting conversion rate when using your product campared to the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
                {
                    name: 'conversion.demo',
                    label: 'Meeting to Demo conversion',
                    description: 'Meeting to Demo conversion measures the percentage of meetings that progress into product demonstrations, reflecting the effectiveness of turning initial engagements into opportunities to showcase your solution.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'conversion_increase.demo',
                    label: 'Demo conversion Increase',
                    description: 'Increased meeting to demo conversion rate when using your product campared to the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
                {
                    name: 'conversion.sales',
                    label: 'Demo to Sales conversion',
                    description: 'Demo to Sales conversion measures the percentage of product demonstrations that result in a closed sale, reflecting the effectiveness of your demos in converting prospects into customers.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'conversion_increase.sales',
                    label: 'Sales conversion Increase',
                    description: 'Increased demo to sales conversion rate when using your product campared to the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
            ]
        }
    },
    "marketing-driven_revenue": {
        purpose: "Quantify how your product increases marketing-driven revenue vs. the competitive alternative by lowering customer acquisition costs (CAC), improving lead quality, and scaling efficient demand generation.",
        introText: "The following driver was identified as potentially impacted by this outcome—click Add Driver to quantify CAC reduction, lead-quality lift, and expected improvement vs. the competitive alternative.",
        driverPath: "marketing_driven",
        driverType: "revenue",
        type: "marketing",
        has_revenue_model: true,
        metrics: {
            subscription: [
                annualContractValue,
                contributionMargin,
                {
                    name: 'customer_acquisition_costs',
                    label: 'Customer Acquisition Costs (CAC)',
                    description: "Customer Acquisition Costs (CAC) represent the total expenses incurred to acquire a new customer, including marketing, sales, and related overhead.",
                    unit: '€',
                    adornment: 'start',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'cac_decrease',
                    label: 'CAC Decrease',
                    description: "Decrease in Customer Acquisition Costs when using your product instead of the competitive alternative.",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
            ],
            transactional: [
                transactionValue,
                transactionFrequency,
                contributionMargin,
                {
                    name: 'customer_acquisition_costs',
                    label: 'Customer Acquisition Costs (CAC)',
                    description: "Customer Acquisition Costs (CAC) represent the total expenses incurred to acquire a new customer, including marketing, sales, and related overhead.",
                    unit: '€',
                    adornment: 'start',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'cac_decrease',
                    label: 'CAC Decrease',
                    description: "Decrease in Customer Acquisition Costs when using your product instead of the competitive alternative.",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
            ]
        }
    },
    customer_lifetime_value: {
        purpose: "Quantify how your product increases customer lifetime value vs. the competitive alternative by improving retention, boosting deal size, and extending contract tenure.",
        introText: "The following driver was identified as potentially impacted by this outcome—click Add Driver to quantify Customer lifetime value uplift versus the competitive alternative.",
        driverPath: "lifetime_value",
        driverType: "revenue",
        type: "clv",
        has_revenue_model: true,
        metrics: {
            subscription: [
                annualContractValue,
                contributionMargin,
                {
                    name: 'clv_change.annual_contract_value',
                    label: 'ACV Increase',
                    description: 'Increase in ACV when using your product instead of the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'full',
                    guided: true,
                    compare: true
                },
                {
                    name: 'contract_length',
                    label: 'Contract length',
                    description: "Average Contract length for your customer's customers.",
                    unit: 'months',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                },
                {
                    name: 'clv_change.contract_length',
                    label: 'Contract length increase',
                    description: 'Increase in in Average Contract length when using your product instead of the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
                {
                    name: 'churn_rate',
                    label: 'Churn rate at end of contract',
                    description: "Churn rate at end of contract represents the percentage of customers who do not renew their contracts once they expire. It reflects customer retention and the effectiveness of your value proposition at the contract's conclusion.",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'clv_change.churn_rate',
                    label: 'Churn rate Decrease',
                    description: 'Decrease in Churn rate when using your product instead of the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: true
                },
            ],
            transactional: [
                transactionValue,
                contributionMargin,
                {
                    name: 'clv_change.transaction_value',
                    label: 'Transaction value Increase',
                    description: 'Increase in Transaction value when using your product instead of the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: true
                },
                transactionFrequency,
                {
                    name: 'clv_change.transaction_frequency',
                    label: 'Transaction frequency Increase',
                    description: 'Increase in Annual transaction frequency when using your product instead of the competitive alternative.',
                    unit: 'times/year',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: true
                },
            ]
        }
    },
    revenue_loss: {
        purpose: "Quantify how your product lowers the risk of lost revenue vs. the competitive alternative by reducing revenue-impacting incidents and resolving them faster.",
        introText: "The following driver was identified as potentially impacted by this outcome—click Add Driver to quantify lost-revenue risk, impact, and potential reduction.",
        driverPath: "revenue_loss",
        driverType: "risk",
        type: "loss",
        has_revenue_model: true,
        metrics: {
            subscription: [
                annualContractValue,
                contributionMargin,
                {
                    name: 'likelihood',
                    label: 'Annual Likelihood',
                    description: 'Annual Likelihood refers to the estimated probability that a revenue loss will occur within one year.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'revenue_loss',
                    label: 'Revenue loss',
                    description: 'Revenue loss quantifies the percentage reduction in revenue, reflecting the financial impact of lost sales or declining performance.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                riskReduction,
            ],
            transactional: [
                transactionValue,
                transactionFrequency,
                contributionMargin,
                {
                    name: 'likelihood',
                    label: 'Annual Likelihood',
                    description: 'Annual Likelihood refers to the estimated probability that a revenue loss will occur within one year.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'revenue_loss',
                    label: 'Revenue loss',
                    description: 'Revenue loss quantifies the percentage reduction in revenue, reflecting the financial impact of lost sales or declining performance.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                riskReduction,
            ]
        }
    },
    litigation_risk: {
        purpose: "Quantify how your product lowers litigation risk vs. the competitive alternative by improving compliance and documentation, reducing violations and disputes, and accelerating resolution.",
        introText: "The following legal exposure areas were identified as potentially impacted by this outcome—click Add Driver to quantify case likelihood, legal/settlement costs, and mitigation.",
        driverPath: "litigation_reduction",
        driverType: "risk",
        type: "litigation",
        has_revenue_model: false,
        metrics: [
            {
                name: 'compliance_costs',
                label: 'Compliance Costs',
                description: 'One-off costs tied to this specific case: policy updates, staff retraining, internal audits, corrective actions, and documentation to demonstrate remediation.',
                unit: '€',
                adornment: 'start',
                width: 'half',
                guided: false,
                compare: false
            },
            {
                name: 'operational_costs',
                label: 'Operational Costs',
                description: 'Internal costs from this case’s disruption: diverted staff time, delays, rework, temporary outsourcing/backfill, and management overhead.',
                unit: '€',
                adornment: 'start',
                width: 'half',
                guided: false,
                compare: false
            },
            {
                name: 'legal_costs',
                label: 'Legal Costs',
                description: 'External legal spend attributable to this case: attorney fees, filings, discovery/e-discovery, expert witnesses, depositions, and court fees.',
                unit: '€',
                adornment: 'start',
                width: 'half',
                guided: false,
                compare: false
            },
            {
                name: 'reputational_costs',
                label: 'Reputational Costs',
                description: 'Per-case brand impact costs: PR/crisis management, customer concessions, retention/reacquisition campaigns, and partner appeasement.',
                unit: '€',
                adornment: 'start',
                width: 'half',
                guided: false,
                compare: false
            },
            {
                name: 'settlement_costs',
                label: 'Settlement Costs',
                description: 'Payments specific to resolving this case: settlements, court-ordered damages, restitution, and any mandated remediation spend.',
                unit: '€',
                adornment: 'start',
                width: 'half',
                guided: false,
                compare: false
            },
            {
                name: 'likelihood',
                label: 'Annual Likelihood',
                description: 'Annual Likelihood refers to the percent chance of a material legal issue occurring within the next 12 months.',
                unit: '%',
                adornment: 'end',
                width: 'half',
                guided: true,
                compare: false
            },
            riskReduction,
        ]
    },
    regulatory_risk: {
        purpose: "Quantify how your product reduces regulatory non-compliance risk vs. the competitive alternative by preventing violations and improving audit/record-keeping to avoid fines.",
        introText: "The following compliance areas were identified as potentially impacted by this outcome—click Add Driver to quantify fine likelihood, fine value, and mitigation.",
        driverPath: "regulatory_reduction",
        driverType: "risk",
        type: "regulatory",
        has_revenue_model: false,
        metrics: []
    },
    time_to_market: {
        purpose: "Quantify how your product accelerates time-to-market vs. the competitive alternative by shortening development, approval, and launch cycles—capturing revenue sooner, improving NPV, and gaining market share.",
        introText: "The following product types were identified as potentially impacted by this outcome—click Add Driver to quantify the product's NPV, and days faster to market vs. the competitive alternative.",
        driverPath: "time_to_market",
        driverType: "revenue",
        type: "ttm",
        has_revenue_model: false,
        metrics: [
            {
                name: 'annual_revenue',
                label: 'Annual revenue',
                description: 'Expected annual revenue of the developed product.',
                unit: '€',
                adornment: 'start',
                width: 'half',
                guided: false,
                compare: false
            },
            contributionMargin,
            {
                name: 'development_cost',
                label: 'Development cost',
                description: 'Total development cost of developed product.',
                unit: '€',
                adornment: 'start',
                width: 'half',
                guided: false,
                compare: false
            },
            {
                name: 'product_life',
                label: 'Product life cycle duration',
                description: "Product life cycle duration refers to the period a product remains active in the market—from its launch through growth, maturity, decline, and until retirement.",
                unit: 'years',
                adornment: 'end',
                width: 'half',
                guided: false,
                compare: false
            },
            {
                name: 'time_faster_to_market',
                label: 'Days faster to market',
                description: "Days shortening of development time when using your product instead of the competitive alternative. E.g. 5 days shorter development time means that the product can be launched earlier and by that bring more profit.",
                unit: 'days',
                adornment: 'end',
                width: 'full',
                guided: true,
                compare: true
            }
        ]
    },
    incremental_revenue: {
        purpose: "Quantify how your product drives incremental revenue vs. the competitive alternative by unlocking new revenue streams—add-ons, premium features, cross-sell/upsell paths—enabling entry into new segments/partnerships, and increasing share of wallet.",
        introText: "The following revenue opportunities were identified as potentially impacted by this outcome—click Add Driver to quantify incremental revenue, attach/upsell rates, and expected uplift vs. the competitive alternative.",
        driverPath: "incremental_revenue",
        driverType: "revenue",
        type: "incremental",
        has_revenue_model: false,
        metrics: []
    },
    facilities_and_infrastructure: {
        purpose: "Quantify how your product reduces Facilities & Infrastructure investment costs vs. the competitive alternative by lowering CapEx, accelerating build/upgrade timelines, and cutting financing/carrying costs through earlier completion and refinancing.",
        introText: "The following Facilities & Infrastructure items were identified as potentially impacted—click Add Driver to quantify CapEx savings, days accelerated to completion, and financing/refinancing cost reductions vs. the competitive alternative.",
        driverPath: "facility_savings",
        driverType: "cost",
        type: "facility",
        has_revenue_model: false,
        metrics: []
    },
    production_lines_and_equipment: {
        purpose: "Quantify how your product reduces Production lines & Equipment investment costs vs. the competitive alternative by lowering CapEx, accelerating build/upgrade timelines, and cutting financing/carrying costs through earlier completion and refinancing.",
        introText: "The following Production lines & Equipment items were identified as potentially impacted—click Add Driver to quantify CapEx savings, days accelerated to completion, and financing/refinancing cost reductions vs. the competitive alternative.",
        driverPath: "equipment_savings",
        driverType: "cost",
        type: "equipment",
        has_revenue_model: false,
        metrics: []
    },
    tools_and_equipment: {
        purpose: "Quantify how your product reduces Tools & Equipment investment costs vs. the competitive alternative by lowering CapEx, accelerating build/upgrade timelines, and cutting financing/carrying costs through earlier completion and refinancing.",
        introText: "The following Tools & Equipment items were identified as potentially impacted—click Add Driver to quantify CapEx savings, days accelerated to completion, and financing/refinancing cost reductions vs. the competitive alternative.",
        driverPath: "equipment_savings",
        driverType: "cost",
        type: "equipment",
        has_revenue_model: false,
        metrics: []
    },
    customer_support: {
        purpose: "Quantify how your product reduces Customer Support cost vs. the competitive alternative by deflecting contacts, shortening time to resolve, boosting first-contact resolution, and cutting compensation/goodwill payouts.",
        introText: "The following Customer Support areas were identified as potentially impacted—click Add Driver to quantify contact reduction, resolution cost savings, and lower compensation payouts vs. the competitive alternative.",
        driverPath: "cs_savings",
        driverType: "cost",
        type: "cs",
        has_revenue_model: false,
        metrics: []
    },
    inventory: {
        purpose: "Quantify how your product reduces Inventory costs vs. the competitive alternative by lowering stock levels, cutting storage/handling fees, and freeing tied-up capital to improve cash flow and return on capital.",
        introText: "The following Inventory areas were identified as potentially impacted—click Add Driver to quantify stock reduction, warehouse cost savings, and capital freed vs. the competitive alternative.",
        driverPath: "inventory_savings",
        driverType: "cost",
        type: "inventory",
        has_revenue_model: false,
        metrics: []
    },
    faster_payment: {
        purpose: "Quantify how your product accelerates cash collection vs. the competitive alternative by reducing DSO—lowering working-capital needs and financing costs.",
        introText: "The following driver were identified as potentially impacted—click Add Driver to quantify % of credit sales, cost of borrowing, and DSO reduction vs. the competitive alternative.",
        driverPath: "payment_savings",
        driverType: "cost",
        type: "payment",
        has_revenue_model: true,
        metrics: {
            subscription: [
                annualContractValue,
                {
                    name: 'share_credit_sales',
                    label: '% credit sales',
                    description: "The percentage of total sales made on credit rather than paid upfront, indicating how much revenue is tied up in receivables.",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'dso_decrease',
                    label: 'DSO reduction',
                    description: "DSO (Days Sales Outstanding) measures the average number of days it takes to collect payment after a sale, where a reduction improves cash flow.",
                    unit: 'days',
                    adornment: 'end',
                    width: 'full',
                    guided: true,
                    compare: true
                },
                {
                    name: 'cost_of_borrowing',
                    label: 'Cost of borrowing',
                    description: "The effective interest rate a company pays on its borrowed funds, including loans, credit lines, or bonds.",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                },

            ],
            transactional: [
                transactionValue,
                transactionFrequency,
                {
                    name: 'share_credit_sales',
                    label: '% credit sales',
                    description: "The percentage of total sales made on credit rather than paid upfront, indicating how much revenue is tied up in receivables.",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
                {
                    name: 'dso_decrease',
                    label: 'DSO reduction',
                    description: "DSO (Days Sales Outstanding) measures the average number of days it takes to collect payment after a sale, where a reduction improves cash flow.",
                    unit: 'days',
                    adornment: 'end',
                    width: 'full',
                    guided: true,
                    compare: true
                },
                {
                    name: 'cost_of_borrowing',
                    label: 'Cost of borrowing',
                    description: "The effective interest rate a company pays on its borrowed funds, including loans, credit lines, or bonds.",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                },
            ]
        }
    },
}

const frequencyTransfer = {
    daily: "day",
    monthly: "month",
    quarterly: "quarter",
    yearly: "year",
};

export const getMetrics = (driver, revenue_model, costStructure = {}, product = {}, selectedCategory = '', selectedType = '') => {
    let metrics = [];

    if (Object.keys(metricsConfig).includes(driver)) {
        if (driver === 'time_to_market') {
            metrics = metricsConfig[driver].metrics;

            if (product.unit.length > 0) {
                const unitsNeeded = {
                    name: 'units_needed',
                    label: `${firstCharToUpper(makePlural(product.unit))} of ${product.name} needed for development`,
                    description: `${firstCharToUpper(makePlural(product.unit))} of ${product.name} needed for development`,
                    unit: makePlural(product.unit),
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                };

                metrics = [...metrics, unitsNeeded];
            }
            if (product.revenue_model === 'subscription') {
                const timeNeeded = {
                    name: 'time_needed',
                    label: `${firstCharToUpper(makePlural(frequencyTransfer[product.frequency]))} subscription of ${product.name} needed for development`,
                    description: `${firstCharToUpper(makePlural(frequencyTransfer[product.frequency]))} subscription of ${product.name} needed for development`,
                    unit: makePlural(frequencyTransfer[product.frequency]),
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                };

                metrics = [...metrics, timeNeeded];
            }
        }
        else if (driver === 'incremental_revenue') {
            const revenueType = costStructure.revenue_types[selectedType];

            metrics = [
                {
                    name: 'revenue_per_multiple',
                    label: `Revenue per ${revenueType?.multiple || ''}`,
                    description: `Average expected revenue per ${revenueType?.multiple || ''} for every new opportunity.`,
                    unit: '€',
                    adornment: 'start',
                    width: 'half',
                    guided: false,
                    compare: false
                },
                {
                    name: 'multiple_value',
                    label: 'Annual opportunity',
                    description: "The annual created opportunity when using your product instead of the competitive alternative. E.g. how large market is opened up for the customer when using your product. Opportunity is however not the same as a sale.",
                    unit: makePlural(revenueType?.multiple || ''),
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: true
                },
                contributionMargin,
                {
                    name: 'probability',
                    label: 'Probability of acheiving a sale or Market share',
                    description: 'Probability of acheiving a sale or the expected market share in the market. E.g. the customer is expected to convert 10% of the opportunities or their market share is expected to be 10%.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                },
            ]

            if (product.unit.length > 0) {
                const unitsNeeded = {
                    name: 'units_needed',
                    label: `${firstCharToUpper(makePlural(product.unit))} of ${product.name} needed for supplying opportunity`,
                    description: `${firstCharToUpper(makePlural(product.unit))} of ${product.name} needed for supplying opportunity`,
                    unit: makePlural(product.unit),
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: false
                };

                metrics = [...metrics, unitsNeeded];
            }

        }
        else if (driver === 'facilities_and_infrastructure' || driver === 'production_lines_and_equipment' || driver === 'tools_and_equipment') {
            const investmentType = costStructure.investment_structure[driver][selectedType];

            metrics = [
                {
                    name: 'cost_per_multiple',
                    label: `Cost per ${investmentType?.multiple}`,
                    description: `Total investment cost per ${investmentType?.multiple}.`,
                    unit: '€',
                    adornment: 'start',
                    width: 'half',
                    guided: false,
                    compare: false
                },
                {
                    name: 'useful_life',
                    label: 'Useful life',
                    description: "Useful life refers to the estimated period during which an asset or product is expected to remain functional and provide economic value before it needs to be replaced or becomes obsolete.",
                    unit: 'years',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                },
                {
                    name: 'share_of_cost',
                    label: '% of cost',
                    description: "Indicates the percentage of cost affected by the efficiency change (saving). E.g., 100% means all of the cost are affected; 50% means half are.",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                },
                {
                    name: 'efficiency_gain',
                    label: 'Savings',
                    description: "Savings relative to your competitive alternative (the one stated when you created your Product).",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                },
                {
                    name: 'project_shortening',
                    label: 'Project shortening',
                    description: "Project shortening (in days) measures the change in project duration achieved through through using your product compared to the competitive alternative. It is used in calculating change in refinancing costs.",
                    unit: 'days',
                    adornment: 'end',
                    width: 'half',
                    guided: true,
                    compare: true
                },
                {
                    name: 'interest_rate_spread',
                    label: 'Interest rate spread',
                    description: "Interest rate spread in refinancing is the difference between the existing loan’s interest rate and the new refinanced rate. A favorable spread occurs when the new rate is lower, leading to cost savings over time, whereas a negative spread indicates a higher cost.",
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                },
            ]

            if (product.unit.length > 0) {
                const unitsNeeded = {
                    name: 'units_needed',
                    label: product.revenue_model === 'transactional' ? `${product.name} needed for project}` : `${firstCharToUpper(makePlural(frequencyTransfer[product.frequency]))} subscription of ${product.name} needed for project`,
                    description: "Quantify the need for your project in completing the project.",
                    unit: product.revenue_model === 'transactional' ? makePlural(product.unit) : makePlural(frequencyTransfer[product.frequency]),
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                };

                metrics = [...metrics, unitsNeeded];
            }
        }
        else if (driver === 'customer_support') {
            const cs_personnel_costs = costStructure.cost_structure.customer_support.personnel_costs;

            const cs_personnel = cs_personnel_costs.multiple.type

            metrics = [
                {
                    name: 'share_contacting',
                    label: `% of customers contacting${product['frequency'].length > 0 ? ` per ${frequencyTransfer[product['frequency']]}` : ''}`,
                    description: `Share of active customers contacting customer service per ${product['frequency'].length > 0 ? frequencyTransfer[product['frequency']] : 'year'}.`,
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                },
                {
                    name: 'contact_rate',
                    label: `Contact rate per customer${product['frequency'].length > 0 ? ` per ${frequencyTransfer[product['frequency']]}` : ''}`,
                    description: `Average number of times a customer is contacting per ${product['frequency'].length > 0 ? frequencyTransfer[product['frequency']] : 'year'}.`,
                    unit: 'contacts',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                },
                {
                    name: 'contact_decrease',
                    label: 'Contact decrease',
                    description: 'Decrease in customers contacting when using your product compared to the competitive alternative.',
                    unit: '%',
                    adornment: 'end',
                    width: 'full',
                    guided: false,
                    compare: true
                },
            ]

            cs_personnel.forEach(type => {
                metrics.push(
                    {
                        name: `min_per_contact.${type}`,
                        label: `Contact duration for ${firstCharToUpper(type)}`,
                        description: 'The average time the employee type is spending on resolving an issue when contacted by a customer, e.g. time on the phone, time emailing, time working in internal systems etc.',
                        unit: 'min',
                        adornment: 'end',
                        width: 'half',
                        guided: false,
                        compare: false
                    }
                )
                metrics.push(
                    {
                        name: `efficiency_gain_contact.${type}`,
                        label: `Efficiency gain for ${firstCharToUpper(type)}`,
                        description: 'Efficiency gain in contact resolution time for the employee type when using your product compared to the competitive alternative.',
                        unit: '%',
                        adornment: 'end',
                        width: 'half',
                        guided: false,
                        compare: true
                    }
                )
            })

            metrics.push(
                {
                    name: 'share_with_compensation',
                    label: '% of contacts with compensation',
                    description: 'Share of contacting customers that are getting some kind of compensation.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: true
                }
            )

            metrics.push(
                {
                    name: 'compensation',
                    label: 'Compensation per contact e.g. monetary or goods',
                    description: 'Compensation represents the average value of benefits—monetary or goods—provided to customers who receive such adjustments.',
                    unit: '€',
                    adornment: 'start',
                    width: 'half',
                    guided: false,
                    compare: true
                }
            )

        }
        else if (driver === 'inventory') {
            const productionCosts = costStructure.cost_structure.operational_costs.warehouse_storage.production_costs

            metrics = [
                {
                    name: 'production_cost',
                    label: `Cost per ${productionCosts.multiple}`,
                    description: `Production cost per ${productionCosts.multiple} is the total cost to make one ${productionCosts.multiple} of output—direct materials + direct labor + an allocated share of manufacturing overhead.`,
                    unit: '€',
                    adornment: 'start',
                    width: 'half',
                    guided: false,
                    compare: false
                },
                {
                    name: 'alternative_returns',
                    label: 'Alternative returns',
                    description: 'Alternative return (warehouse capital): the yield your cash could earn elsewhere if it weren’t tied up in the warehouse—e.g., paying down debt or investing at your hurdle rate.',
                    unit: '%',
                    adornment: 'end',
                    width: 'half',
                    guided: false,
                    compare: false
                },
                {
                    name: 'efficiency_gain',
                    label: 'Stock reduction',
                    description: 'Stock reduction: lowering on-hand inventory to free up cash, cut carrying/holding costs, and reduce obsolescence.',
                    unit: '%',
                    adornment: 'end',
                    width: 'full',
                    guided: true,
                    compare: true
                }
            ]
        }
        else if (driver === 'regulatory_risk') {
            const regulatoryFines = costStructure.risk_structure.regulatory_fines.find(category => category.name === selectedCategory)
            const regulatoryFineType = regulatoryFines?.types.find(type => type.type === selectedType)

            if (!regulatoryFines?.types) {
                metrics = []
            }
            else {
                metrics = [
                    {
                        name: 'rate_value',
                        label: formatString(Object.keys(regulatoryFineType?.calculation_details.calculation)[1]),
                        description: Object.values(regulatoryFineType?.calculation_details.calculation)[1]['description'],
                        unit: '%',
                        adornment: 'end',
                        width: 'half',
                        guided: false,
                        compare: false
                    },
                    {
                        name: 'fine_value',
                        label: 'Fine Value',
                        description: 'Fine Value (regulatory fines) which is the monetary amount imposed per violation when a rule is breached.',
                        unit: '€',
                        adornment: 'start',
                        width: 'half',
                        guided: false,
                        compare: false
                    },
                    riskReduction,
                ]
            }
        }
        else {
            metrics = metricsConfig[driver].has_revenue_model ? metricsConfig[driver].metrics[revenue_model] : metricsConfig[driver].metrics;
        }
    }

    return metrics
}

export const getIntroText = (driver) => {
    let introText = "";

    if (Object.keys(metricsConfig).includes(driver)) {
        introText = metricsConfig[driver].introText;
    }

    return introText
}

export const getPurpose = (driver) => {
    let purpose = "";

    if (Object.keys(metricsConfig).includes(driver)) {
        purpose = metricsConfig[driver].purpose;
    }

    return purpose
}

export const getBasePayload = (driver, revenue_model, positiveDriver, outcome, selectedCategory, selectedType, costStructure, currency) => {
    let driverTypeObject = {}
    driverTypeObject[`${metricsConfig[driver].driverType}_type`] = metricsConfig[driver].type;

    let type = ''
    let costStructureObject = {}
    let driverName = ''
    let multipleObject = {}
    let extraFields = {}

    if (driver === 'inventory') {
        costStructureObject = costStructure.cost_structure.operational_costs.warehouse_storage;

        driverName = `${positiveDriver ? '' : 'Extra '}Inventory${positiveDriver ? ' Savings' : ' costs'} ${formatString(selectedType)}`;
        type = costStructureObject.type;

        multipleObject['multiple_type'] = selectedType;
        multipleObject['multiple'] = costStructureObject.type;
        multipleObject['multiple_value'] = 0;
        multipleObject['cost_per_multiple'] = getSliderInitialValue(costStructureObject, selectedType);
        multipleObject['share_of_cost'] = 100;

        extraFields['cost'] = driver;
        extraFields['sub_cost'] = '';
    }
    else if (driver === 'customer_support') {
        costStructureObject = costStructure.cost_structure.customer_support.personnel_costs;

        driverName = `${positiveDriver ? '' : 'Extra '}Customer Support${positiveDriver ? ' Savings' : ''}`;
        type = costStructureObject.type;

        multipleObject['multiple_type'] = costStructureObject.multiple_type;
        multipleObject['multiple'] = getMultipleInitialValues(costStructureObject);
        multipleObject['multiple_value'] = 0;
        multipleObject['cost_per_multiple'] = getSliderInitialValues(costStructureObject, selectedType);
    }
    else if (driver === 'facilities_and_infrastructure' || driver === 'production_lines_and_equipment' || driver === 'tools_and_equipment') {
        const investmentType = costStructure.investment_structure[driver][selectedType];

        driverName = `${formatString(selectedType)} Project ${positiveDriver ? 'Savings' : 'Extra'}`;
        type = investmentType.type;

        multipleObject['multiple_type'] = selectedType;
        multipleObject['multiple'] = investmentType.type;
        multipleObject['multiple_value'] = 0;
        multipleObject['share_of_cost'] = 100;

        extraFields['cost'] = driver;
        extraFields['sub_cost'] = selectedCategory;
    }
    else if (driver === 'faster_payment') {
        costStructureObject = costStructure.cost_structure.customer_support.personnel_costs;

        driverName = `${positiveDriver ? 'Faster' : 'Slower'} payment`;
        type = 'payment';

        multipleObject['annual_contract_value'] = 0;
        multipleObject['transaction_value'] = 0;
        multipleObject['transaction_frequency'] = 0;
        multipleObject['revenue_model'] = revenue_model;

        extraFields['driver'] = driver;
    }
    else if (driver === 'customer_lifetime_value') {
        driverName = `Lifetime value ${positiveDriver ? 'Increase' : 'Decrease'}`;
        type = 'conversion';

        multipleObject['annual_contract_value'] = 0;
        multipleObject['transaction_value'] = 0;
        multipleObject['transaction_frequency'] = 0;
        multipleObject['revenue_model'] = revenue_model;
    }
    else if (driver === 'incremental_revenue') {
        const revenueType = costStructure.revenue_types[selectedType];

        driverName = `${positiveDriver ? 'Incremental' : 'Decremental'} revenue ${formatString(selectedType)}`;

        multipleObject['multiple'] = revenueType.multiple;
        multipleObject['units_needed'] = 0;
        multipleObject['time_needed'] = 0;

        extraFields['driver'] = driver;
        extraFields['revenue'] = selectedType;
    }
    else if (driver === 'marketing-driven_revenue') {
        driverName = `CAC ${positiveDriver ? 'Decrease' : 'Increase'}`;
        type = 'conversion';

        multipleObject['annual_contract_value'] = 0;
        multipleObject['transaction_value'] = 0;
        multipleObject['transaction_frequency'] = 0;
        multipleObject['revenue_model'] = revenue_model;

        extraFields['driver'] = driver;
    }
    else if (driver === 'sales-driven_revenue') {
        driverName = `Conversion ${positiveDriver ? 'Increase' : 'Decrease'}`;

        multipleObject['annual_contract_value'] = 0;
        multipleObject['transaction_value'] = 0;
        multipleObject['transaction_frequency'] = 0;
        multipleObject['revenue_model'] = revenue_model;

        extraFields['driver'] = driver;
    }
    else if (driver === 'time_to_market') {
        driverName = `${formatString(selectedType)} ${positiveDriver ? 'Faster' : 'Slower'} to market`;

        multipleObject['units_needed'] = 0;
        multipleObject['time_needed'] = 0;

        extraFields['driver'] = driver;
        extraFields['product'] = selectedType;
    }
    else if (driver === 'litigation_risk') {
        driverName = `${formatString(selectedType)} Risk ${positiveDriver ? 'Reduction' : 'Increase'}`;
        type = selectedType;
    }
    else if (driver === 'regulatory_risk') {
        const regulatoryFines = costStructure.risk_structure.regulatory_fines.find(category => category.name === selectedCategory)
        const regulatoryFineType = regulatoryFines.types.find(type => type.type === selectedType)

        multipleObject['likelihood_violation'] = regulatoryFineType.likelihood.violation.value;
        multipleObject['likelihood_fine'] = regulatoryFineType.likelihood.fine.value;
        multipleObject['multiple'] = Object.keys(regulatoryFineType.calculation_details.calculation)[0];
        multipleObject['multiple_value'] = 0;
        multipleObject['rate'] = Object.keys(regulatoryFineType.calculation_details.calculation)[1];

        driverName = `${formatString(selectedType)} Risk ${positiveDriver ? 'Reduction' : 'Increase'}`;
        type = selectedCategory;

        extraFields['sub_type'] = selectedType;
    }
    else if (driver === 'revenue_loss') {
        driverName = `Revenue loss Risk ${positiveDriver ? 'Decrease' : 'Increase'}`;

        multipleObject['annual_contract_value'] = 0;
        multipleObject['transaction_value'] = 0;
        multipleObject['transaction_frequency'] = 0;
        multipleObject['revenue_model'] = revenue_model;
    }

    const basePayload = {
        outcome: outcome,
        ...driverTypeObject,
        driver_name: driverName,
        comment: "",
        positive_driver: positiveDriver,
        currency: currency,
        type: type,
        ...multipleObject,
        ...extraFields
    };

    return { type: metricsConfig[driver].type, basePayload: basePayload }
}

export const getSavedEntry = (driver, selectedOutcome, category, type, valueModel, affectedTypes) => {
    const targetArray = Object.keys(metricsConfig).includes(driver)
        ? valueModel?.[`${metricsConfig[driver].driverType}_value_driver`]?.[metricsConfig[driver].driverPath] : [];

    if (driver === 'time_to_market') {
        return targetArray.find(
            (entry) =>
                entry.outcome === selectedOutcome &&
                entry.product === type
        );
    }
    else if (driver === 'sales-driven_revenue') {
        return targetArray.length > 0;
    }
    else if (driver === 'incremental_revenue') {
        return targetArray.find(
            (entry) =>
                entry.outcome === selectedOutcome &&
                entry.revenue === type
        );
    }
    else if (driver === 'sales-driven_revenue') {
        return targetArray.length > 0;
    }
    else if (driver === 'marketing-driven_revenue') {
        return targetArray.length > 0;
    }
    else if (driver === 'customer_lifetime_value') {
        return targetArray.length > 0;
    }

    else if (driver === 'faster_payment') {
        return targetArray.length > 0;
    }
    else if (driver === 'customer_support') {
        return targetArray.length > 0;
    }
    else if (driver === 'inventory') {
        return targetArray.find(
            (entry) =>
                entry.outcome === selectedOutcome &&
                entry.multiple_type === type
        );
    }
    else if (driver === 'facilities_and_infrastructure' || driver === 'production_lines_and_equipment' || driver === 'tools_and_equipment') {
        return targetArray.find(
            (entry) =>
                entry.outcome === selectedOutcome &&
                entry.sub_cost === category &&
                entry.multiple_type === type
        );
    }

    else if (driver === 'regulatory_risk') {
        return targetArray.find(
            (entry) =>
                entry.outcome === selectedOutcome &&
                entry.type === category &&
                entry.sub_type === type
        );
    }
    else if (driver === 'litigation_risk') {
        return targetArray.find(
            (entry) =>
                entry.outcome === selectedOutcome &&
                entry.type === type
        );
    }
    else if (driver === 'revenue_loss') {
        return targetArray.length > 0;
    }

    return false
};

export const nestDotPaths = (flat) =>
    Object.entries(flat).reduce((acc, [key, val]) => {
        const parts = key.split('.');
        let cur = acc;
        parts.forEach((p, i) => {
            if (i === parts.length - 1) {
                cur[p] = val;
            } else {
                cur[p] ||= {};
                cur = cur[p];
            }
        });
        return acc;
    }, {});